import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ScheduleItem,
  type ScheduleSettings,
  type HolidayInfo,
  formatDateLabel,
} from '@/lib/scheduleUtils';
import {
  type BuildertrendRow,
  buildertrend5MethuenSchedule,
} from '@/lib/buildertrendSeed';
import {
  useBuildertrendImport,
  getConfidenceColor,
  getConfidenceShort,
  type ImportPreviewRow,
} from '@/hooks/useBuildertrendImport';

interface Props {
  projectId: string;
  existingItems: ScheduleItem[];
  settings: ScheduleSettings;
  holidays: HolidayInfo[];
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function BuildertrendImportModal({
  projectId,
  existingItems,
  settings,
  holidays,
  show,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const {
    rows,
    stats,
    toggleRow,
    selectAll,
    deselectAll,
    saveSelected,
    saving,
    saveError,
    saveSuccess,
    clearMessages,
  } = useBuildertrendImport(projectId, existingItems, buildertrend5MethuenSchedule, settings, holidays);

  const [filter, setFilter] = useState<'all' | 'changes' | 'new' | 'exact' | 'high' | 'medium' | 'low' | 'none'>('all');
  const [confirmStep, setConfirmStep] = useState(false);

  const filteredRows = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'changes') return rows.filter((r) => r.hasChanges);
    if (filter === 'new') return rows.filter((r) => r.confidence === 'none');
    return rows.filter((r) => r.confidence === filter);
  }, [rows, filter]);

  const selectedCount = rows.filter((r) => r.selected).length;

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-[#1A1A1A]">{t('btImport.title')}</h2>
            <p className="text-xs text-[#9CA3AF] mt-0.5">
              5 Methuen Road — {t('btImport.itemsFromPDF', { count: buildertrend5MethuenSchedule.length })}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#9CA3AF] cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-100 bg-[#F9FAFB] flex-shrink-0 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full bg-[#059669]"></span>
            <span className="text-[#374151] font-medium">{stats.exact} {t('btImport.filterExact').toLowerCase()}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full bg-[#10B981]"></span>
            <span className="text-[#374151] font-medium">{stats.high} {t('btImport.filterHigh').toLowerCase()}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full bg-[#E8621A]"></span>
            <span className="text-[#374151] font-medium">{stats.medium} {t('btImport.filterMedium').toLowerCase()}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full bg-[#F59E0B]"></span>
            <span className="text-[#374151] font-medium">{stats.low} {t('btImport.filterLow').toLowerCase()}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full bg-[#9CA3AF]"></span>
            <span className="text-[#374151] font-medium">{stats.none} {t('btImport.filterNew').toLowerCase()}</span>
          </div>
          <div className="h-4 w-px bg-gray-200 mx-1"></div>
          <span className="text-xs text-[#6B7280]">
            {t('btImport.selectedOf', { selected: stats.selectedCount, withChanges: stats.withChangesCount })}
          </span>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 px-6 py-2 border-b border-gray-100 flex-shrink-0 flex-wrap">
          {(
            [
              { key: 'all' as const, label: t('btImport.filterAll') },
              { key: 'changes' as const, label: t('btImport.filterChanges') },
              { key: 'new' as const, label: t('btImport.filterNew') },
              { key: 'exact' as const, label: t('btImport.filterExact') },
              { key: 'high' as const, label: t('btImport.filterHigh') },
              { key: 'medium' as const, label: t('btImport.filterMedium') },
              { key: 'low' as const, label: t('btImport.filterLow') },
              { key: 'none' as const, label: t('btImport.filterNoMatch') },
            ] as { key: typeof filter; label: string }[]
          ).map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors ${
                filter === f.key
                  ? 'bg-[#1A1A1A] text-white'
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
          <div className="flex-1"></div>
          <button
            onClick={selectAll}
            className="text-[11px] text-[#E8621A] font-medium cursor-pointer hover:underline whitespace-nowrap"
          >
            {t('btImport.selectAll')}
          </button>
          <button
            onClick={deselectAll}
            className="text-[11px] text-[#6B7280] font-medium cursor-pointer hover:underline whitespace-nowrap"
          >
            {t('btImport.deselectAll')}
          </button>
        </div>

        {/* Preview table */}
        <div className="flex-1 overflow-y-auto px-6 py-3 min-h-0">
          {confirmStep ? (
            <ConfirmSummary rows={rows.filter((r) => r.selected)} onBack={() => setConfirmStep(false)} t={t} />
          ) : (
            <div className="space-y-2">
              {filteredRows.map((row) => (
                <PreviewRowCard
                  key={row.pdfRow.rowNo}
                  row={row}
                  onToggle={() => toggleRow(row.pdfRow.rowNo)}
                  t={t}
                />
              ))}
              {filteredRows.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-sm text-[#9CA3AF]">{t('btImport.noMatchFilter')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
          {saveError && (
            <div className="mb-3 bg-[#FEF2F2] rounded-lg p-2.5 text-xs text-[#EF4444] flex items-start gap-2">
              <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
              <span>{saveError}</span>
            </div>
          )}
          {saveSuccess && (
            <div className="mb-3 bg-[#F0FDF4] rounded-lg p-2.5 text-xs text-[#059669] flex items-start gap-2">
              <i className="ri-check-line flex-shrink-0 mt-0.5"></i>
              <span>{saveSuccess}</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!confirmStep && (
                <span className="text-xs text-[#6B7280]">
                  {t('btImport.willBeImported', { selected: selectedCount, total: rows.length })}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {confirmStep ? (
                <>
                  <button
                    onClick={() => setConfirmStep(false)}
                    disabled={saving}
                    className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-[#374151] text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {t('btImport.back')}
                  </button>
                  <button
                    onClick={async () => {
                      clearMessages();
                      await saveSelected();
                      onSaved();
                    }}
                    disabled={saving || selectedCount === 0}
                    className="px-5 py-2 rounded-xl bg-[#E8621A] text-white text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-[#B84C10] transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        {t('btImport.saving')}
                      </>
                    ) : (
                      <>
                        <i className="ri-check-double-line"></i>
                        {t('btImport.confirmImport')}
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={onClose}
                    disabled={saving}
                    className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-[#374151] text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {t('btImport.cancel')}
                  </button>
                  <button
                    onClick={() => {
                      if (selectedCount === 0) return;
                      setConfirmStep(true);
                    }}
                    disabled={selectedCount === 0}
                    className="px-5 py-2 rounded-xl bg-[#E8621A] text-white text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-[#B84C10] transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <i className="ri-eye-line"></i>
                    {t('btImport.reviewConfirm')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Preview row card ────────────────────────────────────────

function PreviewRowCard({
  row,
  onToggle,
  t,
}: {
  row: ImportPreviewRow;
  onToggle: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const conf = getConfidenceColor(row.confidence);
  const short = getConfidenceShort(row.confidence);

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
        row.selected
          ? row.hasChanges
            ? 'bg-[#FEF0E7]/30 border-[#FDBA74]'
            : 'bg-white border-gray-100'
          : 'bg-gray-50/50 border-gray-100 opacity-60'
      }`}
    >
      {/* Checkbox */}
      <button
        onClick={onToggle}
        className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${
          row.selected
            ? 'bg-[#E8621A] border-[#E8621A] text-white'
            : 'bg-white border-gray-300 hover:border-[#E8621A]'
        }`}
      >
        {row.selected && <i className="ri-check-line text-xs"></i>}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-[#9CA3AF] font-mono">#{row.pdfRow.rowNo}</span>
          <p className="text-xs font-semibold text-[#1A1A1A]">{row.pdfRow.title}</p>
          <span
            className="text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{ backgroundColor: conf.bg, color: conf.text }}
          >
            {short}
          </span>
          {row.hasChanges && row.selected && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FEF0E7] text-[#92400E]">
              {t('btImport.changes')}
            </span>
          )}
        </div>

        <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1">
          {/* Matched item */}
          <div className="text-[11px]">
            <span className="text-[#9CA3AF]">{t('btImport.matched')}:</span>{' '}
            <span className={row.matchedItem ? 'text-[#374151] font-medium' : 'text-[#9CA3AF] italic'}>
              {row.matchedItem ? row.matchedItem.title : t('btImport.newItem')}
            </span>
          </div>

          {/* Current */}
          <div className="text-[11px]">
            <span className="text-[#9CA3AF]">{t('btImport.current')}:</span>{' '}
            <span className="text-[#374151]">
              {row.matchedItem?.start_date ? formatDateLabel(row.matchedItem.start_date) : '—'}
              {' · '}
              {row.matchedItem?.duration_days ?? '—'}d
            </span>
          </div>

          {/* New */}
          <div className="text-[11px]">
            <span className="text-[#9CA3AF]">{t('btImport.new')}:</span>{' '}
            <span className="text-[#1A1A1A] font-medium">
              {formatDateLabel(row.newStartDate)}
              {' · '}
              {row.newDuration}d
            </span>
            {' → '}
            <span className="text-[#6B7280]">{formatDateLabel(row.newEndDate)}</span>
          </div>

          {/* Color */}
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="text-[#9CA3AF]">{t('btImport.color')}:</span>
            <span
              className="inline-block w-3 h-3 rounded-sm border border-gray-200"
              style={{ backgroundColor: row.newColor }}
            ></span>
            <span className="text-[#6B7280] text-[10px]">{row.pdfRow.phase}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Confirm summary ─────────────────────────────────────────

function ConfirmSummary({
  rows,
  onBack,
  t,
}: {
  rows: ImportPreviewRow[];
  onBack: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const toUpdate = rows.filter((r) => r.matchedItem);
  const toCreate = rows.filter((r) => !r.matchedItem);

  return (
    <div className="space-y-4">
      <div className="bg-[#FEF0E7] rounded-xl border border-[#FDBA74] p-4">
        <p className="text-sm font-semibold text-[#92400E]">
          <i className="ri-alert-line mr-1.5"></i>
          {t('btImport.reviewBeforeConfirm')}
        </p>
        <p className="text-xs text-[#92400E]/80 mt-1">
          {t('btImport.reviewDesc', { update: toUpdate.length, create: toCreate.length })}
        </p>
      </div>

      {toUpdate.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
            {t('btImport.itemsToUpdate', { count: toUpdate.length })}
          </h4>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-[#F9FAFB]">
                <tr>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnNo')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnPDFTitle')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnMatchedItem')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnOldStart')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnNewStart')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnDuration')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnColor')}</th>
                </tr>
              </thead>
              <tbody>
                {toUpdate.map((r) => (
                  <tr key={r.pdfRow.rowNo} className="border-t border-gray-50">
                    <td className="px-3 py-2 text-[#9CA3AF] font-mono">{r.pdfRow.rowNo}</td>
                    <td className="px-3 py-2 text-[#1A1A1A] font-medium">{r.pdfRow.title}</td>
                    <td className="px-3 py-2 text-[#374151]">{r.matchedItem!.title}</td>
                    <td className="px-3 py-2 text-[#6B7280]">
                      {r.matchedItem!.start_date ? formatDateLabel(r.matchedItem!.start_date) : '—'}
                    </td>
                    <td className="px-3 py-2 text-[#1A1A1A] font-medium">
                      {formatDateLabel(r.newStartDate)}
                    </td>
                    <td className="px-3 py-2 text-[#374151]">
                      {r.matchedItem!.duration_days}d → {r.newDuration}d
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className="inline-block w-3 h-3 rounded-sm border border-gray-200"
                        style={{ backgroundColor: r.newColor }}
                      ></span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {toCreate.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
            {t('btImport.newItemsToCreate', { count: toCreate.length })}
          </h4>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-[#F9FAFB]">
                <tr>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnNo')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnTitle')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnStart')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnEnd')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnDuration')}</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">{t('btImport.columnPhase')}</th>
                </tr>
              </thead>
              <tbody>
                {toCreate.map((r) => (
                  <tr key={r.pdfRow.rowNo} className="border-t border-gray-50">
                    <td className="px-3 py-2 text-[#9CA3AF] font-mono">{r.pdfRow.rowNo}</td>
                    <td className="px-3 py-2 text-[#1A1A1A] font-medium">{r.pdfRow.title}</td>
                    <td className="px-3 py-2 text-[#374151]">{formatDateLabel(r.newStartDate)}</td>
                    <td className="px-3 py-2 text-[#374151]">{formatDateLabel(r.newEndDate)}</td>
                    <td className="px-3 py-2 text-[#374151]">{r.newDuration}d</td>
                    <td className="px-3 py-2 text-[#6B7280]">{r.pdfRow.phase}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}